/**
 * FacebookMessages Component (OWZ-32)
 *
 * Demonstrates `pages_messaging` with a two-pane Messenger inbox.
 * Reads conversations + messages and lets the admin reply within the
 * 24-hour Meta messaging window.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  Loader2,
  MessageCircle,
  RefreshCw,
  Send,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { facebook } from '@/services/api/admin';

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const otherParticipantName = (conversation, pageId) => {
  const participants = conversation?.participants?.data || [];
  const other = participants.find((p) => p.id !== pageId);
  return other?.name || 'Unknown sender';
};

const FacebookMessages = () => {
  const { toast } = useToast();
  const [conversations, setConversations] = useState([]);
  const [pageId, setPageId] = useState(null);
  const [pageName, setPageName] = useState('');
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [error, setError] = useState(null);
  const [reply, setReply] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [windowExpired, setWindowExpired] = useState(false);

  const fetchConversations = useCallback(async () => {
    setIsLoadingList(true);
    setError(null);
    try {
      const data = await facebook.conversations.list(25);
      setConversations(data.conversations || []);
      setPageId(data.page_id);
      setPageName(data.page_name || '');
    } catch (err) {
      setError(err.message || 'Failed to load conversations');
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const fetchMessages = useCallback(async (conversationId) => {
    setIsLoadingThread(true);
    try {
      const data = await facebook.conversations.messages(conversationId, 25);
      const sorted = [...(data.messages || [])].sort((a, b) => {
        const ta = new Date(a.created_time).getTime();
        const tb = new Date(b.created_time).getTime();
        return ta - tb;
      });
      setMessages(sorted);

      // Check 24h window — find last inbound message
      const lastInbound = [...sorted].reverse().find((m) => m.from?.id !== data.page_id);
      if (lastInbound) {
        const age = Date.now() - new Date(lastInbound.created_time).getTime();
        setWindowExpired(age > 24 * 3600 * 1000);
      } else {
        setWindowExpired(true);
      }
    } catch (err) {
      toast({
        title: 'Could not load thread',
        description: err.message || 'Failed to load messages',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingThread(false);
    }
  }, [toast]);

  const handleSelect = (conversation) => {
    setSelected(conversation);
    setReply('');
    fetchMessages(conversation.id);
  };

  const handleSend = async () => {
    if (!selected || !reply.trim()) return;
    setIsSending(true);
    try {
      await facebook.conversations.reply(selected.id, reply.trim());
      toast({ title: 'Message sent' });
      setReply('');
      await fetchMessages(selected.id);
      fetchConversations();
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes('24')) {
        setWindowExpired(true);
      }
      toast({
        title: 'Send failed',
        description: err.message || 'Could not send message',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Messenger Inbox</CardTitle>
          <CardDescription>
            Read and reply to DMs sent to {pageName || 'your Facebook Page'}.
            Replies are subject to Meta's 24-hour messaging window.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={fetchConversations} aria-label="Refresh">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[400px]">
            <div className="border dark:border-gray-700 rounded-md overflow-hidden">
              <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                Conversations
              </div>
              {isLoadingList ? (
                <div className="py-12 flex justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : conversations.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  No conversations yet.
                </p>
              ) : (
                <ul className="divide-y dark:divide-gray-700 max-h-[480px] overflow-y-auto">
                  {conversations.map((c) => {
                    const isActive = selected?.id === c.id;
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => handleSelect(c)}
                          className={cn(
                            'w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors',
                            isActive && 'bg-blue-50 dark:bg-blue-900/20'
                          )}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium truncate">
                              {otherParticipantName(c, pageId)}
                            </span>
                            {c.unread_count > 0 && (
                              <Badge variant="secondary" className="h-5 px-1.5">
                                {c.unread_count}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {c.snippet || 'No preview'}
                          </p>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500">
                            {formatWhen(c.updated_time)}
                          </p>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="md:col-span-2 border dark:border-gray-700 rounded-md flex flex-col min-h-[400px]">
              {!selected ? (
                <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
                  <div className="text-center">
                    <MessageCircle className="h-10 w-10 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                    <p className="text-sm">Select a conversation to read messages.</p>
                  </div>
                </div>
              ) : isLoadingThread ? (
                <div className="flex-1 flex justify-center items-center">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  <div className="px-3 py-2 border-b dark:border-gray-700 text-sm font-medium">
                    {otherParticipantName(selected, pageId)}
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {messages.length === 0 ? (
                      <p className="text-sm text-center text-gray-500 dark:text-gray-400 py-12">
                        No messages in this thread.
                      </p>
                    ) : (
                      messages.map((m) => {
                        const isFromPage = m.from?.id === pageId;
                        return (
                          <div
                            key={m.id}
                            className={cn(
                              'max-w-[80%] rounded-lg px-3 py-2 text-sm',
                              isFromPage
                                ? 'ml-auto bg-blue-600 text-white'
                                : 'mr-auto bg-gray-100 dark:bg-gray-800 dark:text-gray-100'
                            )}
                          >
                            <p className="whitespace-pre-wrap">{m.message || '(attachment)'}</p>
                            <p className={cn(
                              'text-[10px] mt-1',
                              isFromPage ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                            )}>
                              {formatWhen(m.created_time)}
                            </p>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="border-t dark:border-gray-700 p-3 space-y-2">
                    {windowExpired && (
                      <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded p-2">
                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                        <p>
                          The 24-hour messaging window has expired for this
                          conversation. Meta does not allow standard replies until
                          the user messages again.
                        </p>
                      </div>
                    )}
                    <Textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Write a reply…"
                      rows={2}
                      disabled={windowExpired || isSending}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={handleSend}
                        disabled={!reply.trim() || windowExpired || isSending}
                      >
                        {isSending ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3 mr-1" />
                        )}
                        Send
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FacebookMessages;
